import React, { FC } from 'react'
import { Card, CardActionArea } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { Theme } from '@mui/material/styles'
import { Typography } from '@dg-shared'
import { TypographyType } from './typography/Typography'

export interface CardHeaderItem {
  text: string | string[]
  type?: TypographyType
}

export const useCardStyles = makeStyles()((theme: Theme) => ({
  // Shell
  card: {
    borderRadius: theme.spacing(2),
    transition: '0.2s',
    overflow: 'hidden',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    '&:hover': {
      boxShadow: `0 0 10px ${theme.palette.primary.main}`,
    },
  },

  actionArea: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'stretch',
    height: '100%',
  },

  // Body
  body: {
    padding: theme.spacing(2),
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },

  title: {
    fontWeight: 700,
    marginBottom: theme.spacing(0.25),
  },

  subtitle: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    marginBottom: theme.spacing(1),
  },

  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: theme.spacing(0.75),
    paddingBottom: theme.spacing(0.75),
  },

  rowValueInline: {
    display: 'flex',
    alignItems: 'center',
  },

  copyIcon: {
    fontSize: '1rem',
    marginRight: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    cursor: 'pointer',
  },

  bold: {
    fontWeight: 700,
  },

  actionButton: {
    marginTop: theme.spacing(2),
    borderRadius: '24px',
    width: '100%',
    textTransform: 'none',
  },

  cardHeader: {
    backgroundColor: theme.brand.colors.cardHeader,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: `${theme.spacing(1.5)} ${theme.spacing(3)}`,
  },
}))

interface BaseCardProps {
  header?: React.ReactNode
  headerItems?: CardHeaderItem[]
  children: React.ReactNode
  onClick?: () => void
  testId?: string
  className?: string
  elevation?: number
}

const BaseCard: FC<BaseCardProps> = ({
  header,
  headerItems,
  children,
  onClick,
  testId,
  className,
  elevation = 3,
}) => {
  const { classes, cx } = useCardStyles()

  const resolvedHeader = headerItems ? (
    <div className={classes.cardHeader}>
      {headerItems.map((item, i) => (
        <Typography key={i} type={item.type} color='textCardHeader'>
          {item.text}
        </Typography>
      ))}
    </div>
  ) : (
    header
  )

  const content = (
    <>
      {resolvedHeader}
      {children}
    </>
  )

  return (
    <Card
      className={cx(classes.card, className)}
      elevation={elevation}
      data-testid={onClick ? undefined : testId}
    >
      {onClick ? (
        <CardActionArea className={classes.actionArea} onClick={onClick} data-testid={testId}>
          {content}
        </CardActionArea>
      ) : (
        content
      )}
    </Card>
  )
}

export default BaseCard
